// SpaceAnalysis.jsx — Rich-text editor cards + dynamic-packing PDF (like EntranceAnalysis)
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { X, CheckSquare, Square, ChevronDown, ChevronUp, Download, Save, Edit3, Eye, EyeOff, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { SPACE_ANALYSIS_DEFAULT_DATA, TYPE_LABELS, SPACE_SECTION_LABELS } from './SpaceAnalysisDefaultData';
import { SPACE_NOTES_DEFAULT, parseNotesBlock } from './SpaceNotesData';
import jsPDF from 'jspdf';
import * as martinez from "martinez-polygon-clipping";
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';
import { pickLang, LANGUAGE_OPTIONS } from './i18n/languageOptions';
import { describeSaveError } from './storageUsage';

// NOTE: was a flat, unscoped key shared across every one of a user's projects
// — scoped per-project below (component-level const shadows this).

// ── Type badge colours ──────────────────────────────────────────────────────
const typeBadgeStyle = (type) => {
  if (!type) return { background:'#e0f2fe', color:'#0369a1', border:'1px solid #7dd3fc' };
  const t = type.toLowerCase();
  if (t === 'good')     return { background:'#dcfce7', color:'#16a34a', border:'1px solid #86efac' };
  if (t === 'very bad') return { background:'#fee2e2', color:'#991b1b', border:'1px solid #fca5a5' };
  if (t === 'not good') return { background:'#fee2e2', color:'#dc2626', border:'1px solid #fca5a5' };
  return { background:'#e0f2fe', color:'#0369a1', border:'1px solid #7dd3fc' };
};

// ── Build rich HTML from item fields ───────────────────────────────────────
// Default "Notes" block for a space type (shown once after all instances of
// that space). Spaces with no predefined notes get an empty, ready-to-type body.
const buildNotesHtml = (placementName, lang = 'en') => {
  const heading = pickLang(SPACE_SECTION_LABELS.notes, lang);
  const lines = SPACE_NOTES_DEFAULT[placementName];
  if (!lines || !lines.length) return `<h2>${heading}</h2><p><br></p>`;
  return `<h2>${heading}</h2>` + lines.map(l => `<p>${pickLang(l, lang)}</p>`).join('');
};

// One notes entry per instance of a space (Toilet #1, Toilet #2, ...), so the
// user can enable it for any/all of them. By default only the LAST instance
// of each space type has it enabled — the notes print once, after every
// instance of that space, not repeated inside each one.
const buildNotesItems = (report, savedById = {}, lang = 'en') => {
  const byPlacement = {}, order = [];
  report.forEach(it => {
    if (it.kind === 'notes') return;
    if (!byPlacement[it.placementName]) { byPlacement[it.placementName] = []; order.push(it.placementName); }
    const list = byPlacement[it.placementName];
    if (!list.some(x => x.instance === it.instance)) list.push({ instance: it.instance, customName: it.customName });
  });
  const out = [];
  order.forEach(placementName => {
    const insts = byPlacement[placementName];
    insts.forEach((inst, idx) => {
      const id = `${placementName}_${inst.instance}_notes`;
      const base = { id, kind: 'notes', placementName, instance: inst.instance, customName: inst.customName, direction: '' };
      const saved = savedById[id];
      const defaultHtml = buildNotesHtml(placementName, lang);
      const keepSaved = saved && saved.richHtml && !isStillDefaultSpaceHtml(base, saved.richHtml);
      out.push({
        ...base,
        notesEnabled: saved && typeof saved.notesEnabled === 'boolean' ? saved.notesEnabled : idx === insts.length - 1,
        richHtml: keepSaved ? saved.richHtml : defaultHtml,
      });
    });
  });
  return out;
};

export const buildSpaceHtml = (item, lang = 'en') => {
  if (item.kind === 'notes') return buildNotesHtml(item.placementName, lang);
  const name = item.customName || item.placementName || 'Space';
  const parts = [`<h2>${name}</h2>`];
  const add = (heading, val) => {
    if (val) parts.push(`<h2>${heading}</h2><p>${val}</p>`);
  };
  add(pickLang(SPACE_SECTION_LABELS.zone, lang),     item.zone  || item.direction);
  add(pickLang(SPACE_SECTION_LABELS.type, lang),     pickLang(TYPE_LABELS[item.type], lang) || item.type);
  add(pickLang(SPACE_SECTION_LABELS.impact, lang),   pickLang(item.impact, lang));
  add(pickLang(SPACE_SECTION_LABELS.remedies, lang), pickLang(item.remedies, lang));
  return parts.join('') || '<p><br></p>';
};

// True when `html` is exactly one of the 8 canonical default-language
// renders for this item (i.e. nothing the user has typed over it yet) —
// used to tell "still default, safe to re-translate on a language switch"
// apart from "user customised this, leave it alone".
export const isStillDefaultSpaceHtml = (item, html) => {
  if (!html) return true;
  return LANGUAGE_OPTIONS.some(({ code }) => buildSpaceHtml(item, code) === html);
};

// Default report built straight from the saved space/direction picks (no Edit mode needed): same entries
// generateReportData makes, plus the notes entries. Text is left out — it is rebuilt from each item's own
// data in the selected language when the report is opened or exported.
export const buildDefaultSpaceReport = (selectionsKey, rotation = 0, lang = 'en') => {
  let sel = {};
  try { sel = JSON.parse(localStorage.getItem(selectionsKey) || '{}') || {}; } catch (e) { sel = {}; }
  const { selectedSpaces = {}, spaceDirections = {}, spaceNames = {} } = sel;
  const report = [], seen = new Set();
  Object.keys(selectedSpaces).forEach(spaceName => {
    const count = selectedSpaces[spaceName];
    for (let i = 1; i <= count; i++) {
      const spaceKey = count > 1 ? `${spaceName}_${i}` : spaceName;
      const customName = spaceNames[spaceKey] || (count > 1 ? `${spaceName} #${i}` : spaceName);
      (spaceDirections[spaceKey] || []).forEach(direction => {
        const key = `${spaceName}_${i}_${direction}`;
        if (seen.has(key)) return;
        seen.add(key);
        const vd = (SPACE_ANALYSIS_DEFAULT_DATA[spaceName] || []).find(e => e.zone === direction);
        report.push({
          id: key, placementName: spaceName, customName, instance: i, direction,
          zone: vd ? vd.zone : direction, type: vd ? vd.type : 'Unknown',
          impact: vd ? vd.impact : 'Data not available', remedies: vd ? vd.remedies : 'Please check data source',
          compassRotation: rotation,
        });
      });
    }
  });
  if (!report.length) return [];
  return [...report, ...buildNotesItems(report.map(it => ({ ...it, richHtml: buildSpaceHtml(it, lang) })), {}, lang).map(n => ({ ...n, richHtml: undefined }))];
};

// ── Parse richHtml → [{heading, text}] for PDF ─────────────────────────────
const parseSpaceSections = (html) => {
  if (!html) return [{ heading:'', text:'' }];
  const parts = html.split(/<h2>/i).filter(Boolean);
  return parts.map(part => {
    const m = part.match(/^(.*?)<\/h2>([\s\S]*)/i);
    if (m) {
      return {
        heading: m[1].replace(/<[^>]+>/g, '').trim(),
        text:    m[2].replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      };
    }
    return { heading:'', text: part.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() };
  });
};

// ── Rich text editor ────────────────────────────────────────────────────────
// itemId = the REAL id handed back to onChange (the parent matches items on it);
// syncKey = what decides when the DOM is re-synced from `value` (item + language).
// They used to be one prop with the language glued on, so every edit was reported
// under an id no item had — typing worked on screen but was never saved/printed.
const RichEditor = React.memo(function RichEditor({ itemId, syncKey, value, onChange }) {
  const editorRef   = useRef(null);

  // Only reset innerHTML when switching to a different item, not on every value change.
  // Updating innerHTML on each keypress would reset the cursor position.
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || '<p><br></p>';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey ?? itemId]);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(itemId, editorRef.current.innerHTML);
    }
  }, [itemId, onChange]);

  const execCmd = (cmd) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, null);
    handleInput();
  };

  return (
    <div>
      <div style={{ display:'flex', gap:'4px', padding:'4px 8px', background:'#f8fafc', borderBottom:'1px solid #e2e8f0', flexWrap:'wrap' }}>
        {[['bold','B'],['italic','I'],['underline','U']].map(([cmd, lbl]) => (
          <button key={cmd} onMouseDown={e => { e.preventDefault(); execCmd(cmd); }}
            style={{ padding:'2px 8px', background:'#fff', border:'1px solid #cbd5e1', borderRadius:'3px',
                     fontSize:'11px', fontWeight: cmd==='bold'?700:400, fontStyle: cmd==='italic'?'italic':'normal',
                     cursor:'pointer', color:'#334155' }}>
            {lbl}
          </button>
        ))}
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className="space-editor"
        onInput={handleInput}
        style={{ minHeight:'120px', padding:'10px', outline:'none', fontSize:'0.82rem',
                 lineHeight:1.6, color:'#1e293b', wordBreak:'break-word' }}
      />
    </div>
  );
});

// ── Space Card ──────────────────────────────────────────────────────────────
const SpaceCard = React.memo(function SpaceCard({ item, index, onHtmlChange, onNameChange, onTypeChange, language }) {
  const label = item.customName || item.placementName || 'Space';
  const badge = typeBadgeStyle(item.type);
  // See the [language] effect below — hand-edited content is intentionally
  // never auto-retranslated, so this badge is the only signal the user gets.
  const isEdited = !isStillDefaultSpaceHtml(item, item.richHtml);
  return (
    <div style={{ background:'#ffffff', border:'1.5px solid #e2e8f0', borderRadius:'10px',
                  overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px',
                    background:'#f8fafc', borderBottom:'1px solid #e2e8f0' }}>
        <div style={{ width:30, height:30, borderRadius:6, background:'#00A19D', color:'#fff',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontWeight:700, fontSize:'12px', flexShrink:0 }}>
          {index + 1}
        </div>
        <input
          value={label}
          onChange={e => onNameChange(item.id, e.target.value)}
          style={{ fontSize:'0.9rem', fontWeight:700, color:'#0f172a', flex:1, border:'none',
                   background:'transparent', outline:'none', padding:'2px 4px', borderRadius:'4px',
                   minWidth:0 }}
          onFocus={e => e.target.style.background = '#eef2f7'}
          onBlur={e => e.target.style.background = 'transparent'}
        />
        {isEdited && (
          <span
            title="Edited — won't auto-translate when you switch language. Edit it again in each language you need."
            style={{ fontSize:'10px', color:'#b45309', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'2px 6px', fontWeight:700, flexShrink:0 }}
          >
            Edited
          </span>
        )}
        <input
          value={item.type || ''}
          placeholder="Type"
          onChange={e => onTypeChange(item.id, e.target.value)}
          size={Math.max((item.type || 'Type').length, 4)}
          style={{ ...badge, padding:'2px 8px', borderRadius:'12px', fontSize:'10px', fontWeight:600,
                   flexShrink:0, border: badge.border, textAlign:'center', outline:'none' }}
        />
      </div>
      {/* Including `language` in itemId forces the editor to re-sync its DOM
          from `value` on a language switch — otherwise it only re-reads
          `value` on mount, so a re-translated default would silently never
          appear in the box the user is looking at. */}
      <RichEditor itemId={item.id} syncKey={`${item.id}-${language}-${item._rev || 0}`} value={item.richHtml} onChange={onHtmlChange} />
    </div>
  );
});

// ── Notes Card (one per space instance; prints once, after ALL instances) ───
const NotesCard = React.memo(function NotesCard({ item, onHtmlChange, onToggle, language }) {
  const label = item.customName || item.placementName || 'Space';
  const enabled = !!item.notesEnabled;
  const isEdited = !isStillDefaultSpaceHtml(item, item.richHtml);
  return (
    <div style={{ gridColumn:'1 / -1', background:'#ffffff', border:`1.5px ${enabled ? 'solid #00A19D' : 'dashed #cbd5e1'}`,
                  borderRadius:'10px', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 14px',
                    background: enabled ? '#e6f7f6' : '#f8fafc', borderBottom: enabled ? '1px solid #b7e4e1' : 'none' }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:'0.88rem', fontWeight:700, color:'#0f172a' }}>Additional Notes — {label}</div>
          <div style={{ fontSize:'0.7rem', color:'#64748b', marginTop:'2px' }}>
            {enabled
              ? `Printed once, after all "${item.placementName}" spaces in the report.`
              : 'Disabled — will not appear in the report.'}
          </div>
        </div>
        {isEdited && (
          <span
            title="Edited — won't auto-translate when you switch language. Edit it again in each language you need."
            style={{ fontSize:'10px', color:'#b45309', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:4, padding:'2px 6px', fontWeight:700, flexShrink:0 }}
          >
            Edited
          </span>
        )}
        <label style={{ display:'flex', alignItems:'center', gap:'6px', cursor:'pointer', flexShrink:0,
                        fontSize:'0.75rem', fontWeight:600, color: enabled ? '#00857E' : '#64748b' }}>
          <input type="checkbox" checked={enabled} onChange={e => onToggle(item.id, e.target.checked)}
                 style={{ width:16, height:16, accentColor:'#00A19D', cursor:'pointer' }} />
          {enabled ? 'Enabled' : 'Disabled'}
        </label>
      </div>
      {enabled && <RichEditor itemId={item.id} syncKey={`${item.id}-${language}`} value={item.richHtml} onChange={onHtmlChange} />}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
export default function SpaceAnalysis({
  all32Directions = [],
  compassCenter,
  compassRotation: parentCompassRotation = 0,
  polygonPoints = [],
  baseImage,
  isPolygonComplete,
  canvasWidth = 794,
  canvasHeight = 1123,
  onReportGenerated,
  openModal = false,
  onModalClose = null,
  projectId = null,
  projectName = null,
  language = 'en'
}) {
  // Mobile: this modal's header/toolbar buttons, the 50/50 selection-mode
  // side-by-side panels, the 8-column direction grid, and the edit-mode
  // minmax(360px,...) cards grid were all desktop-only assumptions that
  // either overflowed or became unusably cramped on a phone width. Fixed
  // below via isMobile-gated styles only; desktop keeps every original
  // style untouched (false branch of every ternary is the original value).
  const isMobile = useIsMobile();
  const STORAGE_KEY = `space_analysis_report_data_${projectId || 'new'}`;
  const STORAGE_KEY_SELECTIONS = `space_analysis_selections_${projectId || 'new'}`;
  const A4_WIDTH  = 794;
  const A4_HEIGHT = 1123;

  // No local "showModal" mirror of the openModal prop — that pattern raced
  // against itself (same bug fixed earlier in VastuDevtaGuide.jsx /
  // CutExtensionGuide.jsx): the close-effect depended on `onModalClose`, an
  // inline arrow function the parent recreates every render, so the effect
  // kept re-firing and could close the modal again right after it opened —
  // click once did nothing, needed a second click. `openModal` is used
  // directly everywhere below instead (it's exactly what showModal always
  // mirrored anyway, minus the race).
  const showModal = openModal;
  const handleCloseModal = () => { if (onModalClose) onModalClose(); };

  const [isGenerating, setIsGenerating]   = useState(false);
  const [localRotation, setLocalRotation] = useState(parentCompassRotation || 0);
  const [isSaved,   setIsSaved]   = useState(false);
  const [isSaving,  setIsSaving]  = useState(false);
  const [viewMode,  setViewMode]  = useState('selection');
  // Canvas is drawn at containerSize * zoomLevel (see drawCanvas below) — the
  // desktop default of 1.5x deliberately overflows its own container so
  // desktop users start zoomed-in and legible, then zoom out/scroll as
  // needed. On the already-cramped ~50%-height mobile panel that same 1.5x
  // meant the property was never visible without scrolling first, looking
  // broken/unresponsive. 1x starts the canvas fit to its container instead;
  // the existing zoom in/out buttons still work exactly the same either way.
  const [zoomLevel, setZoomLevel] = useState(isMobile ? 1 : 1.5);
  // Mobile only: the space-list panel (search + expandable direction grids)
  // and the canvas were forced to a fixed 50/50 height split — with a space
  // expanded (its 4x8 direction grid open), the list needed more room than
  // that, so its fields got cramped/hard to tap. Lets the user collapse the
  // canvas entirely to give the fields the full screen, per explicit
  // request, instead of a fixed smaller canvas size.
  const [showMobilePlan, setShowMobilePlan] = useState(true);

  const [editableReportData, setEditableReportData] = useState([]);

  // Whenever the report language changes, re-generate any item's richHtml
  // that is STILL a default (never hand-edited) into the newly selected
  // language, so switching the toolbar's language dropdown actually
  // switches this predefined content instead of leaving it stuck in
  // whatever language it was first generated in. Anything the user has
  // typed their own words into is left completely untouched. This updates
  // what's shown in the editor here; clicking "Save Report" (as with any
  // other edit) is still what pushes the change through to the export.
  useEffect(() => {
    setEditableReportData(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const next = prev.map(item => {
        if (!isStillDefaultSpaceHtml(item, item.richHtml)) return item;
        const translated = buildSpaceHtml(item, language);
        if (translated === item.richHtml) return item;
        changed = true;
        return { ...item, richHtml: translated };
      });
      return changed ? next : prev;
    });
  }, [language]);

  const canvasRef          = useRef(null);
  const reportRef          = useRef(null);
  const containerRef       = useRef(null);
  const canvasContainerRef = useRef(null);
  const [loadedImage, setLoadedImage] = useState(null);
  const [canvasSize, setCanvasSize]   = useState({ width:400, height:600 });

  const spaceTypes  = useMemo(() => Object.keys(SPACE_ANALYSIS_DEFAULT_DATA), []);
  const directions  = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];

  const [selectedSpaces,  setSelectedSpaces]  = useState(() => {
    try { if (!STORAGE_KEY_SELECTIONS) return {};
      const s = localStorage.getItem(STORAGE_KEY_SELECTIONS);
      if (s) return JSON.parse(s).selectedSpaces || {};
    } catch(e) {} return {};
  });
  const [spaceDirections, setSpaceDirections] = useState(() => {
    try { if (!STORAGE_KEY_SELECTIONS) return {};
      const s = localStorage.getItem(STORAGE_KEY_SELECTIONS);
      if (s) return JSON.parse(s).spaceDirections || {};
    } catch(e) {} return {};
  });
  const [expandedSpaces, setExpandedSpaces] = useState({});
  const [spaceNames, setSpaceNames]         = useState(() => {
    try { if (!STORAGE_KEY_SELECTIONS) return {};
      const s = localStorage.getItem(STORAGE_KEY_SELECTIONS);
      if (s) return JSON.parse(s).spaceNames || {};
    } catch(e) {} return {};
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const savedData = localStorage.getItem(STORAGE_KEY);
    if (savedData) setIsSaved(true);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SELECTIONS, JSON.stringify({ selectedSpaces, spaceDirections, spaceNames }));
    } catch(e) {}
  }, [selectedSpaces, spaceDirections, spaceNames, STORAGE_KEY_SELECTIONS]);

  // Report data is intentionally kept in localStorage across sessions so old reports remain accessible

  useEffect(() => { if (showModal) setLocalRotation(parentCompassRotation || 0); }, [showModal, parentCompassRotation]);

  useEffect(() => {
    if (baseImage) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => setLoadedImage(img);
      img.onerror = () => console.error('Failed to load base image');
      img.src = baseImage;
    }
  }, [baseImage]);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const c = containerRef.current;
        setCanvasSize({ width: c.clientWidth - 20, height: c.clientHeight - 20 });
      }
    };
    if (showModal) {
      setTimeout(update, 100);
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
  }, [showModal]);

  useEffect(() => {
    if (showModal && viewMode === 'selection' && showMobilePlan) drawCanvas();
  }, [showModal, viewMode, loadedImage, localRotation, polygonPoints, compassCenter, canvasSize, zoomLevel, showMobilePlan]);

  // ── Canvas helpers ──────────────────────────────────────────────────────
  const calculateIntersection = (center, angle) => {
    if (!isPolygonComplete || !polygonPoints || polygonPoints.length < 3) return null;
    const adjustedAngle = angle + localRotation;
    const angleRad = (adjustedAngle * Math.PI) / 180;
    const rayDir = { x: Math.cos(angleRad), y: Math.sin(angleRad) };
    let nearestIntersection = null;
    let minDistance = Infinity;
    for (let i = 0; i < polygonPoints.length; i++) {
      const j = (i + 1) % polygonPoints.length;
      const p1 = polygonPoints[i], p2 = polygonPoints[j];
      const s = { x: p2.x - p1.x, y: p2.y - p1.y };
      const denom = rayDir.x * s.y - rayDir.y * s.x;
      if (Math.abs(denom) < 1e-10) continue;
      const dx = p1.x - center.x, dy = p1.y - center.y;
      const t = (dx * s.y - dy * s.x) / denom;
      const u = (dx * rayDir.y - dy * rayDir.x) / denom;
      if (t >= 0 && u >= -0.001 && u <= 1.001) {
        const pt = { x: center.x + t * rayDir.x, y: center.y + t * rayDir.y, distance: t };
        if (pt.distance < minDistance && pt.distance > 0.1) { minDistance = pt.distance; nearestIntersection = pt; }
      }
    }
    return nearestIntersection;
  };

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const displayWidth  = canvasSize.width  * zoomLevel;
    const displayHeight = canvasSize.height * zoomLevel;
    canvas.width  = displayWidth;
    canvas.height = displayHeight;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, displayWidth, displayHeight);
    const scaleX = displayWidth / canvasWidth, scaleY = displayHeight / canvasHeight;
    const mainScale = Math.min(scaleX, scaleY);
    const offsetX = (displayWidth  - canvasWidth  * mainScale) / 2;
    const offsetY = (displayHeight - canvasHeight * mainScale) / 2;
    const transformPoint = (p) => ({ x: p.x * mainScale + offsetX, y: p.y * mainScale + offsetY });

    if (loadedImage) {
      // Same padding as FloorPlanImage.js in the editor so polygon aligns with the image
      const pad = { left: 75, right: 75, top: 105, bottom: 105 };
      const availW = canvasWidth  - pad.left - pad.right;
      const availH = canvasHeight - pad.top  - pad.bottom;
      const imgAspect    = loadedImage.width / loadedImage.height;
      const availAspect  = availW / availH;
      let iW, iH, iX, iY;
      if (imgAspect > availAspect) {
        iW = availW; iH = iW / imgAspect;
        iX = pad.left; iY = pad.top + (availH - iH) / 2;
      } else {
        iH = availH; iW = iH * imgAspect;
        iX = pad.left + (availW - iW) / 2; iY = pad.top;
      }
      ctx.save();
      ctx.drawImage(loadedImage, iX * mainScale + offsetX, iY * mainScale + offsetY, iW * mainScale, iH * mainScale);
      ctx.restore();
    }

    if (isPolygonComplete && polygonPoints && polygonPoints.length >= 3) {
      ctx.save();
      ctx.beginPath();
      const fp = transformPoint(polygonPoints[0]);
      ctx.moveTo(fp.x, fp.y);
      for (let i = 1; i < polygonPoints.length; i++) { const p = transformPoint(polygonPoints[i]); ctx.lineTo(p.x, p.y); }
      ctx.closePath();
      ctx.strokeStyle = '#000000'; ctx.lineWidth = 2; ctx.stroke();
      ctx.restore();
    }

    const dirs16 = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
    const filtered = all32Directions.filter(d => dirs16.includes(d.area));

    if (compassCenter && isPolygonComplete && polygonPoints.length >= 3 && filtered.length > 0) {
      const transformedCenter = transformPoint(compassCenter);

      const toMartinezPolygon = (points) => {
        let ring = points.map(p => [p.x, p.y]);
        let area = 0;
        for (let i = 0; i < ring.length; i++) { const j = (i+1) % ring.length; area += ring[i][0]*ring[j][1]; area -= ring[j][0]*ring[i][1]; }
        if (area > 0) ring = ring.reverse();
        if (ring[0][0] !== ring[ring.length-1][0] || ring[0][1] !== ring[ring.length-1][1]) ring.push(ring[0]);
        return [ring];
      };

      const buildSectorWedge = (center, radiusOuter, startAngleDeg, endAngleDeg, steps = 64) => {
        const toRad = (deg) => (deg * Math.PI) / 180;
        let start = toRad(startAngleDeg), end = toRad(endAngleDeg);
        if (end <= start) end += Math.PI * 2;
        const points = [[center.x, center.y]];
        const step = (end - start) / steps;
        for (let i = 0; i <= steps; i++) { const a = start + i * step; points.push([center.x + radiusOuter * Math.cos(a), center.y + radiusOuter * Math.sin(a)]); }
        points.push([center.x, center.y]);
        return [points];
      };

      const clipSectorCanvas = (center, startAngleDeg, endAngleDeg, boundaryPts) => {
        try {
          if (!boundaryPts || boundaryPts.length < 3) return [];
          const xs = boundaryPts.map(p => p.x), ys = boundaryPts.map(p => p.y);
          const corners = [{ x:Math.min(...xs), y:Math.min(...ys) },{ x:Math.max(...xs), y:Math.min(...ys) },{ x:Math.max(...xs), y:Math.max(...ys) },{ x:Math.min(...xs), y:Math.max(...ys) }];
          const dynamicRadius = Math.max(...corners.map(c => Math.hypot(c.x-center.x, c.y-center.y))) * 1.2;
          const sectorPoly   = buildSectorWedge(center, dynamicRadius, startAngleDeg, endAngleDeg);
          const boundaryPoly = toMartinezPolygon(boundaryPts);
          const result = martinez.intersection(sectorPoly, boundaryPoly);
          if (!result || result.length === 0) return [];
          const allRings = [];
          for (const polygon of result) {
            for (const ring of polygon) {
              if (!ring || ring.length < 3) continue;
              const cleaned = (ring[0][0] === ring[ring.length-1][0] && ring[0][1] === ring[ring.length-1][1]) ? ring.slice(0,-1) : ring;
              if (cleaned.length >= 3) allRings.push(cleaned.map(([x,y]) => ({ x, y })));
            }
          }
          return allRings;
        } catch(e) { console.warn('Martinez canvas clip error:', e); return []; }
      };

      filtered.forEach((dir, index) => {
        const nextDir = filtered[(index + 1) % filtered.length];
        const startAngleDeg = dir.angle + localRotation;
        const endAngleDeg   = nextDir.angle + localRotation;
        const clippedRings  = clipSectorCanvas(compassCenter, startAngleDeg, endAngleDeg, polygonPoints);
        clippedRings.forEach(ringPoints => {
          if (!ringPoints || ringPoints.length < 3) return;
          ctx.save();
          ctx.beginPath();
          const first = transformPoint(ringPoints[0]); ctx.moveTo(first.x, first.y);
          for (let i = 1; i < ringPoints.length; i++) { const pt = transformPoint(ringPoints[i]); ctx.lineTo(pt.x, pt.y); }
          ctx.closePath();
          ctx.strokeStyle = '#000000'; ctx.lineWidth = 1; ctx.globalAlpha = 0.6; ctx.stroke(); ctx.globalAlpha = 1;
          ctx.restore();
        });
      });

      filtered.forEach((dir, index) => {
        const nextDir = filtered[(index + 1) % filtered.length];
        let startAngle = dir.angle, endAngle = nextDir.angle;
        if (endAngle < startAngle) endAngle += 360;
        const midAngleDeg = (startAngle + endAngle) / 2;
        const labelIntersection = calculateIntersection(compassCenter, midAngleDeg);
        if (labelIntersection) {
          const tLI = transformPoint(labelIntersection);
          const dirX = tLI.x - transformedCenter.x, dirY = tLI.y - transformedCenter.y;
          const distance = Math.sqrt(dirX*dirX + dirY*dirY);
          if (distance > 5) {
            const ld = distance + 20;
            const labelX = transformedCenter.x + (dirX / distance) * ld;
            const labelY = transformedCenter.y + (dirY / distance) * ld;
            ctx.save();
            ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.beginPath(); ctx.arc(labelX, labelY, 12, 0, Math.PI*2); ctx.fill();
            ctx.font = 'bold 9px Arial'; ctx.fillStyle = '#000000'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(dir.area, labelX, labelY);
            ctx.restore();
          }
        }
      });

      ctx.beginPath(); ctx.arc(transformedCenter.x, transformedCenter.y, 5, 0, Math.PI*2);
      ctx.fillStyle = '#00857E'; ctx.fill(); ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 2; ctx.stroke();
    }
  };

  // ── Zoom ────────────────────────────────────────────────────────────────
  const handleZoomIn    = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut   = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoomLevel(1);

  // ── Selection handlers ──────────────────────────────────────────────────
  const handleSpaceToggle = (spaceName) => {
    setSelectedSpaces(prev => {
      const n = { ...prev };
      if (n[spaceName]) {
        delete n[spaceName];
        const nd = { ...spaceDirections };
        Object.keys(nd).forEach(k => { if (k.startsWith(`${spaceName}_`) || k === spaceName) delete nd[k]; });
        setSpaceDirections(nd);
      } else { n[spaceName] = 1; }
      return n;
    });
  };

  const handleCountChange = (spaceName, delta) => {
    setSelectedSpaces(prev => {
      const cur = prev[spaceName] || 1;
      const next = Math.max(1, cur + delta);
      if (next < cur) {
        const nd = { ...spaceDirections };
        for (let i = next + 1; i <= cur; i++) delete nd[`${spaceName}_${i}`];
        setSpaceDirections(nd);
      }
      return { ...prev, [spaceName]: next };
    });
  };

  const handleDirectionToggle = (spaceKey, direction) => {
    setSpaceDirections(prev => {
      const cur  = prev[spaceKey] || [];
      const next = cur.includes(direction) ? cur.filter(d => d !== direction) : [...cur, direction];
      return { ...prev, [spaceKey]: next };
    });
  };

  const toggleExpand        = (k) => setExpandedSpaces(prev => ({ ...prev, [k]: !prev[k] }));
  const handleSpaceNameChange = (k, v) => setSpaceNames(prev => ({ ...prev, [k]: v }));
  const getSpaceName          = (k, def) => spaceNames[k] || def;
  const hasSelectedDirections = () => Object.values(spaceDirections).some(d => d && d.length > 0);

  // ── Data lookup ─────────────────────────────────────────────────────────
  const getVastuDetails = (placement, direction) => {
    const data = SPACE_ANALYSIS_DEFAULT_DATA[placement];
    if (!data) return null;
    return data.find(e => e.zone === direction);
  };

  // ── Generate report data ────────────────────────────────────────────────
  const generateReportData = useCallback(() => {
    const report = [];
    const seen   = new Set();
    Object.keys(selectedSpaces).forEach(spaceName => {
      const count = selectedSpaces[spaceName];
      for (let i = 1; i <= count; i++) {
        const spaceKey  = count > 1 ? `${spaceName}_${i}` : spaceName;
        const dirs      = spaceDirections[spaceKey] || [];
        const customName = getSpaceName(spaceKey, count > 1 ? `${spaceName} #${i}` : spaceName);
        dirs.forEach(direction => {
          const key = `${spaceName}_${i}_${direction}`;
          if (seen.has(key)) return;
          seen.add(key);
          const vd = getVastuDetails(spaceName, direction);
          report.push({
            id:           key,
            placementName: spaceName,
            customName,
            instance:     i,
            direction,
            zone:         vd ? vd.zone     : direction,
            type:         vd ? vd.type     : 'Unknown',
            impact:       vd ? vd.impact   : 'Data not available',
            remedies:     vd ? vd.remedies : 'Please check data source',
            compassRotation: localRotation,
          });
        });
      }
    });
    return report;
  }, [selectedSpaces, spaceDirections, spaceNames, localRotation]);

  // ── Switch to manage (edit) mode ────────────────────────────────────────
  const switchToManageMode = () => {
    const report = generateReportData();
    if (!report.length) { alert('Please select at least one space and direction before managing report.'); return; }

    // Restore previously saved richHtml (and notes enabled-state) if available
    let savedMap = {};
    const savedById = {};
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) JSON.parse(raw).forEach(s => { if (s.id) { savedMap[s.id] = s.richHtml; savedById[s.id] = s; } });
    } catch(e) {}

    // Keep a saved value only if it's an actual hand-edit — an untouched
    // default (saved in whatever language was selected at the time) is
    // rebuilt fresh in the CURRENTLY selected language instead. Without
    // this, entering Edit/Manage mode after switching languages could pull
    // back old English/other-language text that then got exported as-is.
    const withHtml = report.map(item => {
      const saved = savedMap[item.id];
      const keepSaved = saved && !isStillDefaultSpaceHtml(item, saved);
      return { ...item, richHtml: keepSaved ? saved : buildSpaceHtml(item, language) };
    });

    setEditableReportData([...withHtml, ...buildNotesItems(withHtml, savedById, language)]);
    setViewMode('edit');
  };

  const handleNotesToggle = useCallback((itemId, enabled) => {
    setEditableReportData(prev => prev.map(item => item.id === itemId ? { ...item, notesEnabled: enabled } : item));
  }, []);

  // ── Handle rich text changes ────────────────────────────────────────────
  const handleHtmlChange = useCallback((itemId, html) => {
    setEditableReportData(prev => prev.map(item => item.id === itemId ? { ...item, richHtml: html } : item));
  }, []);

  // ── Handle inline title / type edits ────────────────────────────────────
  // Renaming a card or changing its Type must reach what is printed too: the printed
  // text comes from richHtml, so while that is still the untouched default it is
  // rebuilt from the new value (and _rev bumps so the editor box re-syncs). Text the
  // user has hand-edited is never overwritten.
  const patchItemHeader = (item, patch) => {
    const next = { ...item, ...patch };
    if (!isStillDefaultSpaceHtml(item, item.richHtml)) return next;
    return { ...next, richHtml: buildSpaceHtml(next, language), _rev: (item._rev || 0) + 1 };
  };
  const handleNameChange = useCallback((itemId, name) => {
    setEditableReportData(prev => prev.map(item => item.id === itemId ? patchItemHeader(item, { customName: name }) : item));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);
  const handleTypeChange = useCallback((itemId, type) => {
    setEditableReportData(prev => prev.map(item => item.id === itemId ? patchItemHeader(item, { type }) : item));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // ── Grouped items for edit view ─────────────────────────────────────────
  // The group a card sits under is keyed by its (editable) name — but if that
  // key were taken live, typing a single character in the title would move
  // the card to a different group node immediately, and React would remount
  // it (new position in the tree) instead of just re-rendering it, dropping
  // focus out of whatever input/editor was being typed into. So the grouping
  // *key* is taken from a debounced snapshot (settles ~400ms after typing
  // pauses) while each card's own data is still always the live value —
  // typing itself never lags, only the card's group placement does.
  const [groupKeySnapshot, setGroupKeySnapshot] = useState({});
  useEffect(() => {
    const t = setTimeout(() => {
      const snapshot = {};
      editableReportData.forEach(item => {
        snapshot[item.id] = item.customName || item.placementName;
      });
      setGroupKeySnapshot(snapshot);
    }, 400);
    return () => clearTimeout(t);
  }, [editableReportData]);

  const groupedItems = useMemo(() => {
    const groups = {}, order = [];
    editableReportData.forEach(item => {
      const k = groupKeySnapshot[item.id] ?? (item.customName || item.placementName);
      if (!groups[k]) { groups[k] = { items: [], notes: [] }; order.push(k); }
      (item.kind === 'notes' ? groups[k].notes : groups[k].items).push(item);
    });
    return order.map(k => ({ name: k, items: groups[k].items, notes: groups[k].notes }));
  }, [editableReportData, groupKeySnapshot]);

  // ── Save selections ─────────────────────────────────────────────────────
  const saveReportData = useCallback(() => {
    if (!hasSelectedDirections()) { alert('Please select at least one space and direction before saving.'); return; }
    setIsSaving(true);
    try {
      if (STORAGE_KEY_SELECTIONS) {
        localStorage.setItem(STORAGE_KEY_SELECTIONS, JSON.stringify({ selectedSpaces, spaceDirections, spaceNames }));
      }
      setIsSaved(true);
      setTimeout(() => setIsSaving(false), 500);
    } catch(err) { setIsSaving(false); }
  }, [hasSelectedDirections, selectedSpaces, spaceDirections, spaceNames, STORAGE_KEY_SELECTIONS]);

  // ── Dynamic-packing PDF (heading / description format) ──────────────────
  const downloadReportAsPDF = useCallback(async (reportData) => {
    const data = reportData || editableReportData;
    if (!data.length) { alert('No report data available for download'); return; }
    setIsGenerating(true);
    try {
      const doc    = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
      const a4W    = doc.internal.pageSize.getWidth();
      const a4H    = doc.internal.pageSize.getHeight();
      const genDate = new Date().toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' });

      const getUserPoweredBy = () => {
        try {
          const u = JSON.parse(localStorage.getItem('user_data') || 'null');
          return (u && u.website_url && u.website_url.trim()) ? u.website_url.trim() : 'Powered by VastuAnalyzer.com';
        } catch { return 'Powered by VastuAnalyzer.com'; }
      };

      // PDF type scale — Title/Subtitle/Description = 25/20/16pt everywhere
      // in this export. Title keeps this report's own (black) theme;
      // Subtitle and Description are ALWAYS black.
      const TYPE_TITLE_SIZE = 25, TYPE_SUBTITLE_SIZE = 20, TYPE_DESCRIPTION_SIZE = 16, TYPE_BLACK = [0, 0, 0];

      const addPageFrame = () => {
        doc.setTextColor(100,100,100);
        doc.setFontSize(7); doc.setFont('helvetica','normal');
        doc.text(getUserPoweredBy(), 8, 5);
        doc.text(genDate, a4W - 8, 5, { align:'right' });
        doc.setDrawColor(0,0,0); doc.setLineWidth(0.35);
        doc.rect(8, 8, a4W - 16, a4H - 16, 'S');
        // Title — Title tier, this report's own (black) theme.
        doc.setTextColor(0,0,0); doc.setFontSize(TYPE_TITLE_SIZE); doc.setFont('helvetica','bold');
        const titleY = 20;
        doc.text('Space Analysis Report', a4W / 2, titleY, { align:'center' });
        // Subtitle — Subtitle tier, always black.
        const subtitleY = titleY + 9;
        doc.setFontSize(TYPE_SUBTITLE_SIZE); doc.setFont('helvetica','bold'); doc.setTextColor(...TYPE_BLACK);
        doc.text('Space Analysis  |  Vastu Placement Remedies', a4W / 2, subtitleY, { align:'center' });
        const dividerY = subtitleY + 8;
        doc.setDrawColor(200,200,200); doc.setLineWidth(0.2);
        doc.line(10, dividerY, a4W - 10, dividerY);
      };

      const addFooter = (pageNum) => {
        doc.setTextColor(100,100,100); doc.setFontSize(7); doc.setFont('helvetica','normal');
        doc.text(`Page ${pageNum}`, a4W - 8, a4H - 4, { align:'right' });
      };

      // ── Hindi/Devanagari-safe text rendering ──────────────────────────
      // jsPDF's built-in fonts have no Devanagari glyphs and no shaping engine
      // (matras/conjuncts would render out of order), so any line containing
      // Devanagari characters is rasterised on an offscreen canvas — using the
      // browser's own text shaping — and embedded as an image. Pure-Latin text
      // still goes through doc.text() as before (crisp vector, no size cost).
      // BUG FIX: only matched Devanagari (Hindi/Marathi) — Gujarati/Bengali/
      // Tamil/Kannada/Telugu text failed this test and fell through to
      // jsPDF's Latin-only font, printing as garbled symbols. Broadened to
      // cover all 6 non-Latin scripts this app supports a language for
      // (same fix applied to the shared renderer in Configure/index.js).
      const DEVANAGARI_RE = /[ऀ-ॿ઀-૿ঀ-৿஀-௿ఀ-౿ಀ-೿]/;
      const PX_PER_MM = 96 / 25.4;
      const measureCtx = document.createElement('canvas').getContext('2d');
      const hindiFontCss = (fontSizePt, bold) =>
        `${bold ? '700' : '400'} ${(fontSizePt * 96 / 72).toFixed(2)}px "Noto Sans", "Noto Sans Devanagari", "Noto Sans Gujarati", "Noto Sans Bengali", "Noto Sans Tamil", "Noto Sans Kannada", "Noto Sans Telugu", "Nirmala UI", Arial, sans-serif`;

      const wrapMixedText = (text, fontSizePt, bold, maxWidthMM) => {
        measureCtx.font = hindiFontCss(fontSizePt, bold);
        const maxWidthPx = maxWidthMM * PX_PER_MM;
        const words = text.split(' ');
        const lines = [];
        let cur = '';
        words.forEach(word => {
          const test = cur ? cur + ' ' + word : word;
          if (cur && measureCtx.measureText(test).width > maxWidthPx) {
            lines.push(cur);
            cur = word;
          } else {
            cur = test;
          }
        });
        if (cur) lines.push(cur);
        return lines;
      };

      // Wraps text to maxWidthMM, using canvas metrics for Devanagari lines and
      // jsPDF's own splitTextToSize (unchanged behaviour) for plain Latin text.
      const wrapAny = (text, fontSizePt, bold, maxWidthMM) => {
        if (DEVANAGARI_RE.test(text)) return wrapMixedText(text, fontSizePt, bold, maxWidthMM);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.setFontSize(fontSizePt);
        return doc.splitTextToSize(text, maxWidthMM);
      };

      // Draws one line at (x, yBaseline) in mm, matching doc.text()'s baseline
      // positioning, transparently switching to an image for Devanagari lines.
      const drawLineSmart = (line, x, yBaseline, fontSizePt, bold, rgb) => {
        if (!DEVANAGARI_RE.test(line)) {
          doc.setFont('helvetica', bold ? 'bold' : 'normal');
          doc.setFontSize(fontSizePt);
          doc.setTextColor(...rgb);
          doc.text(line, x, yBaseline);
          return;
        }
        const fontCss = hindiFontCss(fontSizePt, bold);
        measureCtx.font = fontCss;
        const scale = 3; // supersample for crisp print output
        const widthPx  = Math.ceil(measureCtx.measureText(line).width) + 4;
        const heightPx = Math.ceil(fontSizePt * 96 / 72 * 1.4);
        const canvas = document.createElement('canvas');
        canvas.width = widthPx * scale;
        canvas.height = heightPx * scale;
        const ctx = canvas.getContext('2d');
        ctx.scale(scale, scale);
        ctx.font = fontCss;
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = `rgb(${rgb.join(',')})`;
        ctx.fillText(line, 2, heightPx * 0.75);
        const widthMM  = widthPx  / PX_PER_MM;
        const heightMM = heightPx / PX_PER_MM;
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', x, yBaseline - heightMM * 0.75, widthMM, heightMM, undefined, 'FAST');
      };

      const CX        = 12;
      const CW        = a4W - 24;
      // Description-tier (16pt) line height, scaled up from the old 8.5pt
      // body's 4.5mm the same way every other report's LINE_H was rescaled
      // when its font size grew.
      const LINE_H    = 8;
      const ITEM_GAP  = 6;
      const PAGE_TOP  = 41;
      const PAGE_BOT  = a4H - 18;
      let   curY      = PAGE_TOP;
      let   pageNum   = 1;

      addPageFrame();

      const newPage = () => {
        addFooter(pageNum);
        doc.addPage();
        pageNum++;
        addPageFrame();
        curY = PAGE_TOP;
      };

      // ── Group data by customName ──────────────────────────────────────
      const groups = {}, order = [];
      data.filter(item => item.kind !== 'notes').forEach(item => {
        const k = item.customName || item.placementName;
        if (!groups[k]) { groups[k] = []; order.push(k); }
        groups[k].push(item);
      });

      // Enabled "Additional Notes" — printed once per space type, right after
      // the LAST instance of that space (e.g. after Toilet #1 AND #2), then
      // the next space's remedies start.
      const noteItems = data.filter(item => item.kind === 'notes' && item.notesEnabled);
      const notesDone = new Set();
      const drawPlacementNotes = (placementName) => {
        if (notesDone.has(placementName)) return;
        notesDone.add(placementName);
        const list = noteItems
          .filter(n => n.placementName === placementName)
          .map(n => ({ n, block: parseNotesBlock(n.richHtml) }))
          .filter(x => x.block.paras.length);
        list.forEach(({ n, block }) => {
          const title = list.length > 1
            ? `${block.heading || 'Notes'} — ${n.customName || n.placementName}`
            : (block.heading || 'Notes');
          if (curY + ITEM_GAP + LINE_H * 2 > PAGE_BOT) newPage(); else curY += ITEM_GAP;
          drawLineSmart(title, CX + 2, curY + LINE_H, TYPE_DESCRIPTION_SIZE, true, TYPE_BLACK);
          doc.setDrawColor(0,0,0); doc.setLineWidth(0.35);
          doc.line(CX, curY + LINE_H + 1.5, CX + CW, curY + LINE_H + 1.5);
          curY += LINE_H + 3;
          block.paras.forEach(p => {
            wrapAny(p, TYPE_DESCRIPTION_SIZE, false, CW - 10).forEach(line => {
              if (curY + LINE_H > PAGE_BOT) newPage();
              drawLineSmart(line, CX + 5, curY + LINE_H, TYPE_DESCRIPTION_SIZE, false, TYPE_BLACK);
              curY += LINE_H;
            });
            curY += 1.5;
          });
        });
      };
      let prevPlacement = null;

      // ── Estimate section height ───────────────────────────────────────
      const calcSectionsH = (sections) => {
        let h = 0;
        sections.forEach((sec, si) => {
          if (si === 0) return; // skip space name heading
          if (sec.heading) h += LINE_H + 1;
          if (sec.text) {
            const lines = wrapAny(sec.text, TYPE_DESCRIPTION_SIZE, false, CW - 10);
            h += lines.length * LINE_H + 2;
          }
        });
        return Math.max(h, 6);
      };

      let firstGroup = true;

      order.forEach(groupName => {
        const items = groups[groupName];
        const GH = 14; // group header height (Subtitle tier)

        // Moving on to a different space type — close out the previous one
        // with its notes before this space's remedies begin.
        const placementNow = items[0].placementName;
        if (prevPlacement !== null && prevPlacement !== placementNow) drawPlacementNotes(prevPlacement);
        prevPlacement = placementNow;

        // Need space for group header + at least first item summary
        if (!firstGroup) {
          if (curY + ITEM_GAP + GH > PAGE_BOT) newPage();
          else curY += ITEM_GAP;
        }
        firstGroup = false;

        // ── Group heading — this group's item-identity — Subtitle tier,
        // always black + underline ──
        drawLineSmart(groupName, CX + 2, curY + 10, TYPE_SUBTITLE_SIZE, true, TYPE_BLACK);
        doc.setDrawColor(0,0,0); doc.setLineWidth(0.6);
        doc.line(CX, curY + GH, CX + CW, curY + GH);
        curY += GH;

        // ── Items within group ──────────────────────────────────────────
        items.forEach((item, idx) => {
          const sections = parseSpaceSections(item.richHtml || buildSpaceHtml(item, language));
          const sectH   = calcSectionsH(sections);
          // Zone badge is a small decorative pill, not body text — its
          // height estimate stays independent of the Description-tier LINE_H.
          const zoneH   = (item.zone || item.direction) ? 7 : 0;
          const itemH   = zoneH + sectH;

          if (idx > 0) {
            if (curY + ITEM_GAP + itemH > PAGE_BOT) newPage();
            else curY += ITEM_GAP;
          } else {
            curY += 2;
          }

          // Zone direction label — drawn as a small filled pill/badge (not plain
          // text) so it visually reads as a tag, not a duplicate of the "Zone"
          // heading that follows a couple of lines below in the section list.
          if (item.zone || item.direction) {
            const badgeText = `ZONE: ${item.zone || item.direction}`;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            const textW  = doc.getTextWidth(badgeText);
            const padX   = 2.2, badgeH = 4.2;
            const badgeY = curY + 0.3;
            doc.setFillColor(248, 236, 224);
            doc.roundedRect(CX + 2, badgeY, textW + padX * 2, badgeH, 1.1, 1.1, 'F');
            doc.setTextColor(124, 66, 28);
            doc.text(badgeText, CX + 2 + padX, badgeY + badgeH - 1.2);
            curY += zoneH;
          }

          // Sections (skip sections[0] = space name) — Description tier
          // throughout, always black.
          sections.forEach((sec, si) => {
            if (si === 0) return;

            if (sec.heading) {
              if (curY + LINE_H + 1 > PAGE_BOT) newPage();
              drawLineSmart(sec.heading, CX + 2, curY + LINE_H, TYPE_DESCRIPTION_SIZE, true, TYPE_BLACK);
              curY += LINE_H + 1;
            }

            if (sec.text) {
              const lines = wrapAny(sec.text, TYPE_DESCRIPTION_SIZE, false, CW - 10);
              lines.forEach(line => {
                if (curY + LINE_H > PAGE_BOT) newPage();
                drawLineSmart(line, CX + 5, curY + LINE_H, TYPE_DESCRIPTION_SIZE, false, TYPE_BLACK);
                curY += LINE_H;
              });
              curY += 2;
            }
          });
        });
      });
      if (prevPlacement !== null) drawPlacementNotes(prevPlacement);

      addFooter(pageNum);

      const fname = (projectName || 'Space_Analysis').replace(/[^a-zA-Z0-9_ -]/g,'').trim().replace(/\s+/g,'_');
      doc.save(`${fname}_Space_Analysis.pdf`);

    } catch(err) {
      console.error('PDF Generation Error:', err);
      alert('Error generating PDF: ' + err.message);
    } finally {
      setIsGenerating(false);
    }
  }, [editableReportData, projectName]);

  // ── Quick PDF from selection mode ────────────────────────────────────────
  const quickDownloadPDF = useCallback(() => {
    const report = generateReportData();
    if (!report.length) { alert('Please select at least one space and direction first'); return; }
    const withHtml = report.map(item => ({ ...item, richHtml: buildSpaceHtml(item, language) }));
    const withNotes = [...withHtml, ...buildNotesItems(withHtml, {}, language)];
    setEditableReportData(withNotes);
    setTimeout(() => downloadReportAsPDF(withNotes), 100);
  }, [generateReportData, downloadReportAsPDF]);

  // ── Save report to localStorage ──────────────────────────────────────────
  const captureAndSendReport = useCallback(async () => {
    if (!editableReportData.length) { alert('No report data available'); return; }
    try {
      // Slim save: an untouched default text is not stored (it is rebuilt from the item's own data in the
      // current language when the report is opened or exported), only hand-edited text is kept — this keeps
      // the saved report small so it does not fill the browser's localStorage.
      const slimData = editableReportData.map(it => (isStillDefaultSpaceHtml(it, it.richHtml) ? { ...it, richHtml: undefined } : it));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(slimData));
      if (onReportGenerated) {
        onReportGenerated({
          layerId:    'space-analysis-report',
          reportData: editableReportData,
          images:     [],
          imageData:  null,
          timestamp:  new Date().toISOString()
        });
      }
      alert('Report saved successfully!');
      if (onModalClose) onModalClose();
    } catch(err) {
      console.error('Error saving report:', err);
      alert('Error saving report: ' + describeSaveError(err));
    }
  }, [editableReportData, onReportGenerated]);

  const filteredSpaces     = spaceTypes.filter(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
  const has32DirectionData = all32Directions && all32Directions.length > 0;

  const leave = useLeaveConfirm({
    onSave: () => { if (viewMode === 'edit') captureAndSendReport(); else { saveReportData(); handleCloseModal(); } },
    onLeave: handleCloseModal,
  });

  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ padding:'0px' }}>
      {leave.dialog}
      {showModal && (
        /* z-index bumped above the mobile bottom toolbar (10100) and
           sheets (10200+) — this full-screen modal used to render behind
           them on phone widths when opened from the footer nav, making it
           look like it never opened. */
        <div style={{ position:'fixed', top:0, left:0, width:'100vw', height:'100vh',
                      background:'#000', zIndex:10500, display:'flex', flexDirection:'column', overflow:'hidden' }}>

          {/* ── Header ── */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                        padding: isMobile ? '8px 10px' : '8px 16px', background:'#00A19D', borderBottom:'1px solid #ffffff',
                        minHeight:'56px', gap: isMobile ? '8px' : '16px' }}>
            <div style={{ minWidth: isMobile ? 'auto' : '180px' }}>
              <h2 style={{ color:'#ffffff', margin:0, fontSize: isMobile ? '13px' : '14px', fontWeight:600 }}>
                Space Analysis {viewMode === 'edit' && '— Manage Report'}
              </h2>
            </div>

            <div style={{ display:'flex', gap: isMobile ? '6px' : '8px', flexWrap:'wrap' }}>
              {viewMode === 'selection' ? (
                <>
                  <button onClick={leave.requestClose}
                    style={{ background:'#dc3545', color:'#fff', padding: isMobile ? '8px 10px' : '8px 12px', border:'none', borderRadius:'4px', cursor:'pointer' }}>
                    <X size={16} />
                  </button>

                  <button onClick={switchToManageMode} disabled={!hasSelectedDirections()}
                    style={{ background: !hasSelectedDirections() ? '#555':'#00857E', color:'#fff',
                             padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px',
                             cursor: !hasSelectedDirections() ? 'not-allowed':'pointer',
                             display:'flex', alignItems:'center', gap:'6px', fontSize:'12px',
                             fontWeight:600, opacity: !hasSelectedDirections() ? 0.5:1 }}>
                    {isMobile ? (<><Edit3 size={14} /><Save size={14} /></>) : (<><Edit3 size={14} /> Edit / <Save size={14} /> Save</>)}
                  </button>

                  <button onClick={quickDownloadPDF} disabled={!hasSelectedDirections() || isGenerating}
                    style={{ background: (!hasSelectedDirections() || isGenerating) ? '#555':'#00857E', color:'#fff',
                             padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px',
                             cursor: (!hasSelectedDirections() || isGenerating) ? 'not-allowed':'pointer',
                             display:'flex', alignItems:'center', gap:'6px', fontSize:'12px',
                             fontWeight:600, opacity: (!hasSelectedDirections() || isGenerating) ? 0.5:1 }}>
                    {isGenerating
                      ? (isMobile ? <span className="spinner" /> : <><span className="spinner" /> Generating...</>)
                      : (isMobile ? <Download size={14} /> : <><Download size={14} /> Download PDF</>)}
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setViewMode('selection')}
                    style={{ background:'#6c757d', color:'#fff', padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px',
                             cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:600 }}>
                    {isMobile ? <Eye size={14} /> : <><Eye size={14} /> Back to Selection</>}
                  </button>

                  <button onClick={() => downloadReportAsPDF(editableReportData)} disabled={isGenerating}
                    style={{ background: isGenerating ? '#555':'#00857E', color:'#fff',
                             padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px',
                             cursor: isGenerating ? 'not-allowed':'pointer',
                             display:'flex', alignItems:'center', gap:'6px', fontSize:'12px',
                             fontWeight:600, opacity: isGenerating ? 0.5:1 }}>
                    {isGenerating
                      ? (isMobile ? <span className="spinner" /> : <><span className="spinner" /> Generating...</>)
                      : (isMobile ? <Download size={14} /> : <><Download size={14} /> Download PDF</>)}
                  </button>

                  <button onClick={captureAndSendReport}
                    style={{ background:'#28a745', color:'#fff', padding: isMobile ? '8px 10px' : '8px 14px', border:'none', borderRadius:'4px',
                             cursor:'pointer', display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:600 }}>
                    {isMobile ? <Save size={14} /> : <><Save size={14} /> Save Report</>}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', flex:1, overflow:'hidden', height:'calc(100vh - 56px)' }}>

            {/* ════ SELECTION MODE ════ */}
            {viewMode === 'selection' ? (
              <>
                {/* Left — space list */}
                <div className="space-scroll-panel"
                  style={{ width: isMobile ? '100%' : '50%', height: isMobile ? (showMobilePlan ? '50%' : '100%') : 'auto', background:'#C7F2F1', display:'flex', flexDirection:'column',
                           borderRight: isMobile ? 'none' : '1px solid #333', borderBottom: isMobile ? '1px solid #333' : 'none', overflow:'hidden' }}>
                  <div style={{ padding:'10px 16px', background:'#00A19D', borderBottom:'1px solid #222', flexShrink:0, display:'flex', alignItems:'center', gap:'8px' }}>
                    <input type="text" placeholder="Search spaces..." value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      style={{ flex:1, padding:'8px 12px', background:'#c7f2f1', border:'1px solid #fff',
                               borderRadius:'4px', color:'#00A19D', fontSize:'12px', outline:'none' }} />
                    {/* Mobile only: collapse the canvas to give this list
                        panel the full screen while filling in space names/
                        directions, per explicit request. */}
                    {isMobile && (
                      <button onClick={() => setShowMobilePlan(v => !v)} title={showMobilePlan ? 'Hide Plan' : 'Show Plan'}
                        style={{ background: showMobilePlan ? 'rgba(255,255,255,0.18)' : '#fff', color: showMobilePlan ? '#fff' : '#00857E', border:'1px solid #fff', borderRadius:'6px', padding:'7px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px', fontSize:'11px', fontWeight:700, flexShrink:0 }}>
                        {showMobilePlan ? <EyeOff size={13}/> : <Eye size={13}/>} Plan
                      </button>
                    )}
                  </div>
                  <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', padding:'12px',
                                display:'flex', flexDirection:'column', gap:'8px' }}>
                    {filteredSpaces.map(space => {
                      const count      = selectedSpaces[space] || 0;
                      const isSelected = count > 0;
                      return (
                        <div key={space}>
                          <div onClick={() => { if (!isSelected) handleSpaceToggle(space); }}
                            style={{ background:'#00A19D', color:'#fff', border:'1px solid #fff',
                                     borderRadius:'6px', padding:'8px 12px', cursor: isSelected ? 'default' : 'pointer',
                                     display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1 }}>
                              {isSelected ? <CheckSquare size={16} color="#fff" /> : <Square size={16} color="#fff" />}
                              <span style={{ fontWeight: isSelected ? 600:400, fontSize:'11px' }}>{space}</span>
                            </div>
                            {isSelected && (
                              <div style={{ display:'flex', alignItems:'center', gap:'4px' }} onClick={e => e.stopPropagation()}>
                                <button onClick={e => { e.stopPropagation(); handleCountChange(space,-1); }} disabled={count<=1}
                                  style={{ background:'#00A19D', color:'#fff', border:'none', borderRadius:'3px',
                                           width:20, height:20, cursor: count<=1?'not-allowed':'pointer', fontSize:'12px' }}>−</button>
                                <span style={{ color:'#fff', fontWeight:'bold', fontSize:'12px', minWidth:'16px', textAlign:'center' }}>{count}</span>
                                <button onClick={e => { e.stopPropagation(); handleCountChange(space,1); }}
                                  style={{ background:'#00A19D', color:'#fff', border:'none', borderRadius:'3px',
                                           width:20, height:20, cursor:'pointer', fontSize:'12px' }}>+</button>
                                <button onClick={e => { e.stopPropagation(); handleSpaceToggle(space); }}
                                  style={{ background:'rgba(220,53,69,0.8)', color:'#fff', border:'none', borderRadius:'3px',
                                           width:20, height:20, cursor:'pointer', fontSize:'12px', marginLeft:'4px',
                                           display:'flex', alignItems:'center', justifyContent:'center' }}>
                                  <X size={10} />
                                </button>
                              </div>
                            )}
                          </div>

                          {isSelected && Array.from({ length:count }, (_,i) => {
                            const spaceKey        = count > 1 ? `${space}_${i+1}` : space;
                            const selectedDirs    = spaceDirections[spaceKey] || [];
                            const isExpanded      = expandedSpaces[spaceKey];
                            return (
                              <div key={spaceKey} style={{ marginTop:'4px', background:'#a3c9c9', borderRadius:'4px',
                                                           border: selectedDirs.length>0 ? '1px solid #00857E':'1px solid #333', flexShrink:0 }}>
                                <div onClick={() => toggleExpand(spaceKey)}
                                  style={{ padding:'6px 10px', background:'#00A19D', cursor:'pointer',
                                           display:'flex', justifyContent:'space-between', alignItems:'center',
                                           borderBottom: isExpanded ? '1px solid #333':'none' }}>
                                  <div style={{ display:'flex', alignItems:'center', gap:'6px', flex:1 }}>
                                    <input type="text"
                                      value={getSpaceName(spaceKey, count>1 ? `${space} #${i+1}` : space)}
                                      onChange={e => handleSpaceNameChange(spaceKey, e.target.value)}
                                      onClick={e => e.stopPropagation()}
                                      style={{ background:'#00A19D', border:'1px solid #fff', borderRadius:'3px',
                                               padding:'3px 6px', color:'#fff', fontSize:'10px', fontWeight:600,
                                               outline:'none', minWidth:'100px' }} />
                                    {selectedDirs.length > 0 && (
                                      <span style={{ color:'#00857E', fontSize:'9px', background:'#fff', padding:'2px 5px', borderRadius:'6px' }}>
                                        {selectedDirs.length}
                                      </span>
                                    )}
                                  </div>
                                  {isExpanded ? <ChevronUp size={12} color="#00857E" /> : <ChevronDown size={12} color="#666" />}
                                </div>
                                {isExpanded && (
                                  <div style={{ padding:'8px' }}>
                                    <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(4,1fr)' : 'repeat(8,1fr)', gap:'3px' }}>
                                      {directions.map(dir => {
                                        const isDirSel = selectedDirs.includes(dir);
                                        return (
                                          <div key={dir} onClick={() => handleDirectionToggle(spaceKey, dir)}
                                            style={{ background: isDirSel ? '#00857E':'#fff',
                                                     border: isDirSel ? '1px solid #00857E':'1px solid #444',
                                                     borderRadius:'3px', padding:'5px 2px', cursor:'pointer',
                                                     fontSize:'9px', color: isDirSel ? '#fff':'#00857E',
                                                     textAlign:'center', fontWeight: isDirSel ? 600:400,
                                                     minHeight:'24px', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                            {dir}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Right — canvas (mobile: collapsible via the "Plan" toggle above) */}
                {(!isMobile || showMobilePlan) && (
                <div style={{ width: isMobile ? '100%' : '50%', height: isMobile ? '50%' : 'auto', background:'#111', display:'flex', flexDirection:'column', position:'relative' }}>
                  <div style={{ position:'absolute', top:'10px', right:'10px', zIndex:100,
                                display:'flex', gap:'8px', background:'rgba(0,0,0,0.7)',
                                padding:'8px', borderRadius:'6px', border:'1px solid #00857E' }}>
                    <button onClick={handleZoomOut} disabled={zoomLevel<=0.5}
                      style={{ background: zoomLevel<=0.5 ? '#333':'#00857E', color: zoomLevel<=0.5 ? '#666':'#fff',
                               border:'none', borderRadius:'4px', padding:'6px 10px',
                               cursor: zoomLevel<=0.5 ? 'not-allowed':'pointer',
                               display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', fontWeight:600 }}>
                      <ZoomOut size={14} />
                    </button>
                    <button onClick={handleResetZoom}
                      style={{ background:'#444', color:'#fff', border:'1px solid #00857E', borderRadius:'4px',
                               padding:'6px 10px', cursor:'pointer',
                               display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', fontWeight:600 }}>
                      <Maximize2 size={14} /> {Math.round(zoomLevel * 100)}%
                    </button>
                    <button onClick={handleZoomIn} disabled={zoomLevel>=3}
                      style={{ background: zoomLevel>=3 ? '#333':'#00857E', color: zoomLevel>=3 ? '#666':'#fff',
                               border:'none', borderRadius:'4px', padding:'6px 10px',
                               cursor: zoomLevel>=3 ? 'not-allowed':'pointer',
                               display:'flex', alignItems:'center', gap:'4px', fontSize:'11px', fontWeight:600 }}>
                      <ZoomIn size={14} />
                    </button>
                  </div>

                  <div ref={canvasContainerRef} className="canvas-scroll-container"
                    style={{ flex:1, overflow:'auto',
                             display:'flex', alignItems: zoomLevel===1 ? 'center':'flex-start',
                             justifyContent: zoomLevel===1 ? 'center':'flex-start',
                             padding:'10px', background:'#ffffff' }}>
                    {has32DirectionData ? (
                      <canvas ref={canvasRef} style={{ display:'block', borderRadius:'4px' }} />
                    ) : (
                      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                                    color:'#666', textAlign:'center', padding:'20px' }}>
                        <div style={{ fontSize:'48px', marginBottom:'16px' }}>📐</div>
                        <div style={{ fontSize:'14px', color:'#888' }}>32-Direction data not available</div>
                        <div style={{ fontSize:'11px', marginTop:'8px' }}>Set up polygon and compass first</div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </>

            ) : (

              /* ════ EDIT / MANAGE MODE ════ */
              <div style={{ width:'100%', background:'#f1f5f9', overflowY:'auto', padding: isMobile ? '14px 10px' : '24px 20px' }}>
                {groupedItems.length === 0 ? (
                  <div style={{ textAlign:'center', color:'#64748b', padding:'60px 20px', fontSize:'14px' }}>
                    No report data yet. Go back and select spaces & directions.
                  </div>
                ) : (
                  groupedItems.map(group => (
                    <div key={group.name} style={{ marginBottom:'28px' }}>
                      {/* Group header */}
                      <div style={{ background:'#00A19D', color:'#fff', padding: isMobile ? '8px 12px' : '9px 16px',
                                    borderRadius:'8px 8px 0 0', fontWeight:700, fontSize: isMobile ? '0.85rem' : '0.92rem',
                                    letterSpacing:'0.02em', marginBottom:'0' }}>
                        {group.name}
                      </div>
                      {/* Cards grid */}
                      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(360px,1fr))',
                                    gap: isMobile ? '10px' : '14px', padding: isMobile ? '10px' : '14px', background:'#e8f4f3',
                                    borderRadius:'0 0 8px 8px', border:'1px solid #c7eded', borderTop:'none' }}>
                        {group.items.map((item, idx) => (
                          <SpaceCard key={item.id} item={item} index={idx} onHtmlChange={handleHtmlChange}
                            onNameChange={handleNameChange} onTypeChange={handleTypeChange} language={language} />
                        ))}
                        {group.notes.map(n => (
                          <NotesCard key={n.id} item={n} onHtmlChange={handleHtmlChange}
                            onToggle={handleNotesToggle} language={language} />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        .spinner { width:12px; height:12px; border:2px solid #fff; border-top-color:transparent; border-radius:50%; animation:spin 1s linear infinite; display:inline-block; }

        .space-editor:focus { outline:none; }
        .space-editor { text-align:left !important; }
        .space-editor h2:first-child { font-size:1rem; font-weight:800; color:#0f172a; margin:0 0 10px; border-bottom:2px solid #000000; padding-bottom:5px; text-align:left !important; }
        .space-editor h2 { font-size:0.82rem; font-weight:700; color:#1e293b; margin:10px 0 2px; text-align:left !important; }
        .space-editor p  { margin:2px 0 4px; color:#374151; font-size:0.82rem; line-height:1.55; text-align:left !important; }
        .space-editor div, .space-editor span, .space-editor li { text-align:left !important; }

        .space-scroll-panel::-webkit-scrollbar { width:12px; }
        .space-scroll-panel::-webkit-scrollbar-track { background:#0a0a0a; border-left:1px solid #333; }
        .space-scroll-panel::-webkit-scrollbar-thumb { background:#00857E; border-radius:6px; border:2px solid #0a0a0a; }
        .space-scroll-panel::-webkit-scrollbar-thumb:hover { background:#00857E; }

        .canvas-scroll-container::-webkit-scrollbar { width:12px; height:12px; }
        .canvas-scroll-container::-webkit-scrollbar-track { background:#c7f2f1; }
        .canvas-scroll-container::-webkit-scrollbar-thumb { background:#00857E; border-radius:6px; border:2px solid #c7f2f1; }
        .canvas-scroll-container::-webkit-scrollbar-thumb:hover { background:#00857E; }
        .canvas-scroll-container::-webkit-scrollbar-corner { background:#c7f2f1; }
      `}</style>
    </div>
  );
}
